"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const lodash_clonedeep_1 = tslib_1.__importDefault(require("lodash.clonedeep"));
const to_case_1 = tslib_1.__importDefault(require("to-case"));
const utils_1 = require("../utils");
const Collection_1 = tslib_1.__importDefault(require("./Collection"));
const EventEmitter_1 = tslib_1.__importDefault(require("../EventEmitter"));
const uuid_1 = require("uuid");
class Model extends EventEmitter_1.default {
    constructor(props = {}) {
        super();
        this.providerName = 'default';
        this.loading = false;
        this.submittableProps = [];
        this.performSafeRequestGraphql = utils_1.performSafeRequestGraphql;
        this.performSafeRequestREST = utils_1.performSafeRequestREST;
        this._exists = false;
        Object.assign(this, this.defaults(), props);
        Object.assign(this, props);
        if (typeof this.hydrateProps === 'function') {
            console.warn('Deprecated: Use property variables or constructor instead. `hydrateProps` will be removed in the next versions.');
            this.hydrateProps(props);
        }
    }
    generateUuid() {
        this.uuid = uuid_1.v4();
    }
    get doRequest() {
        return utils_1.config(this.providerName).graphql ? utils_1.performSafeRequestGraphql : utils_1.performSafeRequestREST;
    }
    get className() {
        return this.constructor.name;
    }
    get displayClassName() {
        return this.className.toLowerCase();
    }
    get defaultMethod() {
        return 'post';
    }
    get identifier() {
        return utils_1.config(this.providerName).graphql ? 'uuid' : 'id';
    }
    exists() {
        return this._exists || Boolean(this.__typename) || Boolean(this.id);
    }
    defaults() {
        return {};
    }
    hydrate(item) {
        return new this.constructor(item);
    }
    getMethod(_opts) {
        return null;
    }
    toCollection(skipEmpty = false) {
        return (new Collection_1.default(this)).filter(i => !!i || !skipEmpty);
    }
    toArray(skipEmpty = false) {
        return this.toCollection(skipEmpty).all();
    }
    getClassName() {
        return this.constructor.name;
    }
    async getInputFields(inputTypeName = `${this.getClassName()}Input`) {
        return utils_1.getSchemaTypeFields(inputTypeName, this.providerName);
    }
    async getUpdateVariables(inputTypeName) {
        const keys = await this.getInputFields(inputTypeName);
        const result = {};
        for (const key of keys) {
            let value = this[key];
            if (value instanceof Collection_1.default) {
                value = value.all();
            }
            if (Array.isArray(value)) {
                result[key] = await Promise.all(value.map(async (model) => ((model instanceof Model) ? await model.getUpdateVariables() : await model)));
                continue;
            }
            if (value instanceof Model) {
                result[key] = await value.getUpdateVariables();
                continue;
            }
            result[key] = await value;
        }
        return result;
    }
    toSubmittable(props = this.submittableProps) {
        if (!props)
            return [];
        if (Array.isArray(props) && !props.length)
            return [];
        return this.toCollection().only(props).all();
    }
    toSubmittableFormData(props = this.submittableProps) {
        const fd = new FormData();
        if (!props || (Array.isArray(props) && !props.length))
            return fd;
        this.toCollection().only(props).each((item, index) => {
            fd.append(index, item);
        });
        return fd;
    }
    async save() {
        return this.exists() ? this.update() : this.create();
    }
    async create(params, mutation) {
        const resolvedRequest = this.resolveRequest(mutation, this.createMutation);
        const resolvedParams = await this.resolveParams(params);
        if (utils_1.config(this.providerName).graphql) {
            return this.hydrate(await this.mutate(resolvedRequest, resolvedParams));
        }
        return this.hydrate(await this.mutate(resolvedRequest, resolvedParams));
    }
    async update(params, mutation) {
        const resolvedRequest = this.resolveRequest(mutation, this.updateMutation);
        const resolvedParams = await this.resolveParams(params);
        if (utils_1.config(this.providerName).graphql) {
            return this.hydrate(await this.mutate(resolvedRequest, resolvedParams));
        }
        return this.hydrate(await this.mutate(resolvedRequest, resolvedParams));
    }
    async delete(mutation, params) {
        const resolvedRequest = this.resolveRequest(mutation, this.deleteMutation);
        const forget = result => this.markNotExists() && result;
        if (utils_1.config(this.providerName).graphql) {
            return this.mutate(resolvedRequest, params || {
                uuid: this.uuid,
            }).then(forget);
        }
        return this.mutate(resolvedRequest, params || {
            id: this.id,
        }, 'delete').then(forget);
    }
    async mutate(mutationOrUrl, params, method = this.defaultMethod) {
        if (utils_1.config(this.providerName).graphql) {
            return this.beforeMutate()
                .then(this.performSafeRequestGraphql.bind(this, mutationOrUrl, params, this.providerName, null))
                .finally(this.afterMutate.bind(this))
                .finally(this.markExists.bind(this));
        }
        const resolvedUrl = await utils_1.getUrl({ method, url: mutationOrUrl, params });
        const resolvedMethod = await this.getMethod({ method, url: resolvedUrl, params });
        return this.beforeMutate()
            .then(this.performSafeRequestREST.bind(this, resolvedUrl, params, resolvedMethod || method, null))
            .finally(this.afterMutate.bind(this))
            .finally(this.markExists.bind(this));
    }
    clone() {
        return lodash_clonedeep_1.default(this).markNotExists();
    }
    markExists() {
        this._exists = true;
        return this;
    }
    markNotExists() {
        this._exists = false;
        return this;
    }
    async beforeMutate() {
        this.loading = true;
        return this;
    }
    afterMutate() {
        this.loading = false;
        return this;
    }
    resolveRequest(request, defaultRequest) {
        if (request)
            return request;
        return typeof defaultRequest === 'function' ? defaultRequest() : defaultRequest;
    }
    async resolveParams(params) {
        if (utils_1.config(this.providerName).graphql) {
            const model = await this.getUpdateVariables();
            return {
                [to_case_1.default.camel(this.className)]: model,
            };
        }
        return params;
    }
}
exports.default = Model;
//# sourceMappingURL=Model.js.map